import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { User } from '@/modules/shared/types'
import { decodeSessionClaims } from './session-claims'

/**
 * The one session store (auth-unification U2 / Platform Architecture V2
 * §3.2): both the staff (password/code) and client (code) audiences read and
 * write this same store now that both get a refreshable session (U1). It
 * holds just the short-lived access `token` (+ staff's `user` projection,
 * kept for the existing staff shell) — the `audience` claim decoded off that
 * token is the single source of truth for which shell (staff vs portal) is
 * active, via `selectAudience`/`selectContactId` below. There is only ever
 * one live session at a time; logging into the other audience replaces it.
 *
 * Link-landing UX state (`linkToken`/`target`/`storeName`) is NOT session
 * identity and lives in the separate `link-context.store`.
 */

/** Placeholder used before authentication resolves and after logout. Never
 *  rendered to an authenticated user — route guards key off `token`. */
const ANON_USER: User = { id: '', name: '', email: '', role: 'member', organizationId: '' }

export type SessionStatus = 'idle' | 'loading' | 'ready' | 'unauthenticated'

interface SessionState {
  token: string | null
  user: User
  isAuthenticated: boolean
  sessionStatus: SessionStatus
  setSession: (token: string, user?: User) => void
  setToken: (token: string | null) => void
  setSessionStatus: (status: SessionStatus) => void
  clearSession: () => void
  logout: () => void
}

const noopStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      user: ANON_USER,
      token: null,
      isAuthenticated: false,
      sessionStatus: 'idle',
      setSession: (token, user) =>
        set({
          token,
          user: user ?? ANON_USER,
          isAuthenticated: true,
          sessionStatus: 'ready',
        }),
      setToken: (token) => set({ token }),
      setSessionStatus: (sessionStatus) => set({ sessionStatus }),
      clearSession: () =>
        set({
          token: null,
          user: ANON_USER,
          isAuthenticated: false,
          sessionStatus: 'unauthenticated',
        }),
      logout: () =>
        set({
          user: ANON_USER,
          token: null,
          isAuthenticated: false,
          sessionStatus: 'unauthenticated',
        }),
    }),
    {
      name: 'scc-session',
      partialize: (s) => ({ token: s.token }),
      merge: (persisted, current) => {
        const p = (persisted as { token?: string | null }) ?? {}
        return {
          ...current,
          token: p.token ?? null,
        }
      },
      storage: createJSONStorage(() => (typeof window !== 'undefined' ? localStorage : noopStorage)),
    },
  ),
)

/** Decoded `audience` claim off the current token — the single source of
 * "which shell" (staff vs client/portal) a session belongs to. `null` when
 * there's no token or it doesn't decode. */
export function selectAudience(s: Pick<SessionState, 'token'>): 'staff' | 'client' | null {
  if (!s.token) return null
  return decodeSessionClaims(s.token)?.audience ?? null
}

/** Decoded `contactId` claim off the current (client-audience) token. */
export function selectContactId(s: Pick<SessionState, 'token'>): number | null {
  if (!s.token) return null
  const claims = decodeSessionClaims(s.token)
  return typeof claims?.contactId === 'number' ? claims.contactId : null
}
